import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  getAllSupportedTimezones,
  searchTimezones,
  findTimezoneOption,
} from '../utils/timezoneUtils';
import './TimezoneSelector.scss';

interface TimezoneSelectorProps {
  value: string;
  onChange: (tz: string) => void;
  error?: string;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const allTimezones = useMemo(() => getAllSupportedTimezones(), []);

  // 100ms debounce for search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 100);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filtered timezone list
  const filteredTimezones = useMemo(() => {
    return searchTimezones(debouncedQuery, allTimezones);
  }, [debouncedQuery, allTimezones]);

  // Selected option details (matches exact, alias e.g. Asia/Calcutta -> Asia/Kolkata, or formats dynamically)
  const selectedOption = useMemo(() => {
    return findTimezoneOption(value, allTimezones);
  }, [allTimezones, value]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery('');
      setDebouncedQuery('');
    }
  }, [isOpen]);

  const handleSelect = (tzId: string) => {
    onChange(tzId);
    setIsOpen(false);
  };

  return (
    <div className="timezone-selector-container" ref={containerRef}>
      <button
        type="button"
        className={`timezone-trigger-btn ${isOpen ? 'active' : ''} ${error ? 'has-error' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="tz-label">
          {selectedOption ? selectedOption.label : value || 'Select timezone'}
        </span>
        <svg
          className={`chevron-icon ${isOpen ? 'rotated' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {error && <span className="tz-inline-error">{error}</span>}

      {isOpen && (
        <div className="timezone-dropdown" role="listbox">
          <div className="tz-search-box">
            <svg
              className="search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="tz-search-input"
              placeholder="Search city or timezone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="btn-clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                &times;
              </button>
            )}
          </div>

          <div className="tz-options-list">
            {filteredTimezones.length === 0 ? (
              <div className="no-tz-found">No timezones found</div>
            ) : (
              filteredTimezones.map((tz) => {
                const isSelected = tz.id === value;
                return (
                  <button
                    key={tz.id}
                    type="button"
                    className={`tz-option-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(tz.id)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="tz-option-label">{tz.label}</span>
                    {isSelected && (
                      <svg
                        className="check-icon"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
